import { useEffect, useState, useMemo, useCallback } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StripeSyncReport from "@/components/StripeSyncReport";
import AnalyticsDrilldown from "@/components/AnalyticsDrilldown";
import {
  Users, Store, ShieldCheck, Calendar, Send, DollarSign, Star,
  UserPlus, CalendarPlus, FileText, CreditCard, Activity, RefreshCw, Mail,
} from "lucide-react";

// Admin gate: trust backend `is_admin` (source of truth = ADMIN_EMAIL env).
const POLL_MS = 10_000;

const fmt$ = (cents) => {
  const dollars = (cents || 0) / 100;
  return dollars >= 1000
    ? `$${(dollars / 1000).toFixed(1)}k`
    : `$${dollars.toFixed(2)}`;
};

const relTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.floor((Date.now() - then) / 1000);
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

function Kpi({ icon: Icon, label, value, sub, testId, accent = "var(--terracotta)", onClick, category }) {
  const clickable = typeof onClick === "function";
  const Wrapper = clickable ? "button" : "div";
  return (
    <Wrapper
      data-testid={testId}
      data-drilldown-category={category}
      onClick={clickable ? () => onClick(category) : undefined}
      type={clickable ? "button" : undefined}
      className={`rounded-lg border p-5 flex flex-col gap-2 text-left transition ${clickable ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5" : ""}`}
      style={{
        backgroundColor: "#fff",
        borderColor: "rgba(0,0,0,0.08)",
      }}
      title={clickable ? "Click to drill into this category" : undefined}
    >
      <div className="flex items-center gap-2">
        <div
          className="p-1.5 rounded-md"
          style={{ backgroundColor: `${accent}14`, color: accent }}
        >
          <Icon size={16} />
        </div>
        <span
          className="text-[10px] uppercase tracking-widest"
          style={{ color: "var(--ink-soft)" }}
        >
          {label}
        </span>
      </div>
      <div className="font-serif text-3xl leading-none">{value}</div>
      {sub && (
        <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
          {sub}
        </div>
      )}
    </Wrapper>
  );
}

function SignupsChart({ data }) {
  const max = useMemo(
    () => Math.max(1, ...data.map((d) => d.total)),
    [data]
  );
  const totalLast7 = data.slice(-7).reduce((s, d) => s + d.total, 0);
  const totalLast30 = data.reduce((s, d) => s + d.total, 0);

  return (
    <div
      data-testid="signups-chart"
      className="rounded-lg border p-5"
      style={{ backgroundColor: "#fff", borderColor: "rgba(0,0,0,0.08)" }}
    >
      <div className="flex items-baseline justify-between">
        <div>
          <div
            className="text-[10px] uppercase tracking-widest"
            style={{ color: "var(--ink-soft)" }}
          >
            Signups — last 30 days
          </div>
          <div className="font-serif text-2xl mt-1">
            {totalLast30}{" "}
            <span
              className="text-sm font-sans"
              style={{ color: "var(--ink-soft)" }}
            >
              total · {totalLast7} this week
            </span>
          </div>
        </div>
      </div>
      <div className="mt-6 flex items-end gap-1 h-36">
        {data.map((d) => {
          const vh = (d.vendors / max) * 100;
          const oh = (d.organizers / max) * 100;
          return (
            <div
              key={d.date}
              title={`${d.date} · ${d.vendors} vendors · ${d.organizers} organizers`}
              className="flex-1 flex flex-col-reverse gap-0.5 min-w-0"
            >
              <div
                style={{
                  height: `${vh}%`,
                  backgroundColor: "var(--terracotta)",
                  minHeight: d.vendors > 0 ? "2px" : 0,
                  borderRadius: "2px",
                }}
              />
              <div
                style={{
                  height: `${oh}%`,
                  backgroundColor: "var(--moss)",
                  minHeight: d.organizers > 0 ? "2px" : 0,
                  borderRadius: "2px",
                }}
              />
            </div>
          );
        })}
      </div>
      <div
        className="flex items-center gap-4 mt-3 text-xs"
        style={{ color: "var(--ink-soft)" }}
      >
        <span className="flex items-center gap-1.5">
          <span
            className="w-2 h-2 rounded-sm"
            style={{ backgroundColor: "var(--terracotta)" }}
          />
          Vendors
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="w-2 h-2 rounded-sm"
            style={{ backgroundColor: "var(--moss)" }}
          />
          Organizers
        </span>
      </div>
    </div>
  );
}

function ActivityIcon({ type }) {
  const map = {
    signup: UserPlus,
    event_created: CalendarPlus,
    application: FileText,
    payment: CreditCard,
    rating: Star,
  };
  const Icon = map[type] || Activity;
  const color = {
    signup: "var(--moss)",
    event_created: "var(--terracotta)",
    application: "#8a827a",
    payment: "#2b6b4a",
    rating: "#c48b2e",
  }[type] || "var(--ink-soft)";
  return (
    <div
      className="p-1.5 rounded-md shrink-0 mt-0.5"
      style={{ backgroundColor: `${color}18`, color }}
    >
      <Icon size={14} />
    </div>
  );
}

function ActivityFeed({ items }) {
  if (!items.length) {
    return (
      <div
        className="text-sm text-center py-10"
        style={{ color: "var(--ink-soft)" }}
      >
        No activity yet. When users sign up, post events, apply, or pay —
        you&apos;ll see them stream in here.
      </div>
    );
  }
  return (
    <ul
      data-testid="activity-feed"
      className="divide-y"
      style={{ borderColor: "rgba(0,0,0,0.06)" }}
    >
      {items.map((a, i) => (
        <li
          key={`${a.type}-${a.at}-${i}`}
          className="py-3 flex items-start gap-3"
          data-testid={`activity-item-${i}`}
        >
          <ActivityIcon type={a.type} />
          <div className="flex-1 min-w-0">
            <div className="text-sm">{a.message}</div>
            <div
              className="text-[11px] mt-0.5"
              style={{ color: "var(--ink-soft)" }}
            >
              {relTime(a.at)}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function AdminAnalytics() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);
  const [drilldownCat, setDrilldownCat] = useState(null);
  const openDrilldown = useCallback((cat) => setDrilldownCat(cat), []);
  const [notifPref, setNotifPref] = useState(null);
  const [notifInput, setNotifInput] = useState("");
  const [notifBusy, setNotifBusy] = useState(false);
  const [notifEditing, setNotifEditing] = useState(false);

  const loadNotifPref = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/settings/notification-email");
      setNotifPref(data);
      setNotifInput(data.override || data.effective || "");
    } catch { /* silent — feature is optional */ }
  }, []);

  const saveNotifPref = async () => {
    const email = notifInput.trim().toLowerCase();
    if (!email.includes("@")) {
      const { toast } = await import("sonner");
      toast.error("Enter a valid email address.");
      return;
    }
    setNotifBusy(true);
    try {
      await api.put("/admin/settings/notification-email", { email });
      const { toast } = await import("sonner");
      toast.success(`Digests will now land at ${email}`);
      setNotifEditing(false);
      await loadNotifPref();
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setNotifBusy(false); }
  };

  const clearNotifPref = async () => {
    setNotifBusy(true);
    try {
      await api.delete("/admin/settings/notification-email");
      const { toast } = await import("sonner");
      toast.success("Reverted to your login email");
      await loadNotifPref();
    } catch (e) { /* ignore */ }
    finally { setNotifBusy(false); }
  };

  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      // Cache-buster: append a timestamp so no proxy/CDN/browser ever serves
      // a stale response. Backend also sends Cache-Control: no-store now, but
      // this is a belt + suspenders fix for the Feb 2026 "counts stuck" bug.
      const { data } = await api.get(`/admin/analytics?_t=${Date.now()}`);
      setData(data);
      setErr("");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRefreshing(false);
    }
  }, []);

  const sendSignupDigest = async () => {
    if (digestBusy) return;
    setDigestBusy(true);
    try {
      const { data } = await api.post("/admin/signup-digest/send",
        { dry_run: false, hours: 24 });
      const { toast } = await import("sonner");
      // If Resend rejected the send, surface it — otherwise we'd silently
      // "succeed" and the admin would keep wondering why no email arrives.
      const status = data?.result?.status;
      const sendOk = status === "sent" || status === "dev";
      const target = data?.recipient || "your inbox";
      if (!sendOk) {
        const detail = data?.result?.error || data?.result?.reason || status || "delivery failed";
        toast.error(`Digest not delivered to ${target}: ${detail}`);
      } else if (status === "dev") {
        toast.info(`Dev mode — email logged for ${target} (Resend key not configured).`);
      } else if (data?.count === 0) {
        toast.success(`Digest sent to ${target} — 0 signups in the last 24h. Enjoy the calm.`);
      } else {
        toast.success(`Digest sent to ${target} · ${data.count} new signup${data.count === 1 ? "" : "s"}`);
      }
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send digest");
    } finally {
      setDigestBusy(false);
    }
  };

  useEffect(() => {
    if (!user || !user.is_admin) return;
    load();
    loadNotifPref();
    const t = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(t);
  }, [user, load, loadNotifPref]);

  if (loading) return null;
  if (!user || !user.is_admin) return <Navigate to="/" replace />;

  return (
    <div className="mx-auto max-w-7xl px-6 py-14" data-testid="admin-analytics-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="overline" style={{ color: "var(--terracotta)" }}>Admin · Analytics</div>
          <h1 className="font-serif text-5xl mt-3">The pulse of AddVendTour.</h1>
          <p
            className="mt-3 text-sm max-w-xl"
            style={{ color: "var(--ink-soft)" }}
          >
            Business snapshot + live activity feed. Auto-refreshes every 10 seconds.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={sendSignupDigest}
            disabled={digestBusy}
            data-testid="signup-digest-send-btn"
            title="Send today's signup digest to your inbox now"
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border"
            style={{
              borderColor: "rgba(0,0,0,0.12)",
              backgroundColor: "#fff",
              color: "var(--ink)",
            }}
          >
            <Mail size={14} className={digestBusy ? "animate-pulse" : ""} />
            {digestBusy ? "Sending…" : "Email today's signups"}
          </button>
          <button
            type="button"
            onClick={() => load(false)}
            disabled={refreshing}
            data-testid="analytics-refresh"
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border"
            style={{
              borderColor: "rgba(0,0,0,0.12)",
              backgroundColor: "#fff",
              color: "var(--ink)",
            }}
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Notification email preference — where analytics + digest emails
          land. DB-backed override on the ADMIN_EMAIL env var, so admins can
          point reports at any inbox without touching prod config. */}
      {notifPref && (
        <div
          data-testid="digest-recipient-panel"
          className="mt-4 rounded-lg border p-3 sm:p-4 flex flex-wrap items-center gap-3"
          style={{ borderColor: "rgba(0,0,0,0.08)", backgroundColor: "#fff" }}
        >
          <Mail size={14} style={{ color: "var(--avt-purple)" }} />
          {!notifEditing ? (
            <>
              <span className="text-xs sm:text-sm" style={{ color: "var(--ink-soft)" }}>
                Analytics emails currently land at:
              </span>
              <span
                data-testid="digest-recipient-effective"
                className="text-xs sm:text-sm font-semibold px-2 py-0.5 rounded"
                style={{
                  backgroundColor: notifPref.override ? "rgba(132,204,22,0.14)" : "rgba(107,47,186,0.08)",
                  color: notifPref.override ? "#4B7A0A" : "var(--avt-purple)",
                }}
              >
                {notifPref.effective || "no recipient set"}
              </span>
              <button
                type="button"
                data-testid="digest-recipient-edit"
                onClick={() => { setNotifEditing(true); setNotifInput(notifPref.override || notifPref.effective || ""); }}
                className="text-xs underline"
                style={{ color: "var(--avt-purple)" }}
              >
                {notifPref.override ? "Change" : "Send to a different inbox"}
              </button>
              {notifPref.override && (
                <button
                  type="button"
                  data-testid="digest-recipient-reset"
                  onClick={clearNotifPref}
                  disabled={notifBusy}
                  className="text-xs underline"
                  style={{ color: "var(--ink-soft)" }}
                >
                  Reset to login email
                </button>
              )}
              {notifPref.override && (
                <span className="text-[10px] uppercase tracking-widest ml-auto"
                      style={{ color: "var(--ink-soft)" }}>
                  Custom
                </span>
              )}
            </>
          ) : (
            <>
              <input
                type="email"
                data-testid="digest-recipient-input"
                value={notifInput}
                onChange={(e) => setNotifInput(e.target.value)}
                placeholder="Nmcclure01988@gmail.com"
                className="text-sm px-3 py-1.5 rounded border flex-1 min-w-[220px]"
                style={{ borderColor: "rgba(0,0,0,0.15)" }}
              />
              <button
                type="button"
                data-testid="digest-recipient-save"
                onClick={saveNotifPref}
                disabled={notifBusy}
                className="text-xs px-3 py-1.5 rounded-full text-white"
                style={{ background: "var(--avt-purple)" }}
              >
                {notifBusy ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                data-testid="digest-recipient-cancel"
                onClick={() => setNotifEditing(false)}
                className="text-xs underline"
                style={{ color: "var(--ink-soft)" }}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      )}

      {err && (
        <div
          data-testid="analytics-error"
          className="mt-6 text-sm rounded-md p-3"
          style={{ backgroundColor: "rgba(31,93,52,0.08)", color: "var(--terracotta)" }}
        >
          {err}
        </div>
      )}

      {!data && !err && (
        <div
          className="mt-10 text-sm"
          style={{ color: "var(--ink-soft)" }}
          data-testid="analytics-loading"
        >
          Loading numbers…
        </div>
      )}

      {data && (
        <>
          {/* KPI Row */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi
              icon={Users}
              label="Total users"
              value={data.totals.users}
              sub={`${data.totals.vendors} vendors · ${data.totals.organizers} organizers`}
              testId="kpi-users"
              accent="var(--terracotta)"
              category="users"
              onClick={openDrilldown}
            />
            <Kpi
              icon={Store}
              label="Maker Pro"
              value={data.totals.pro_active}
              sub={`MRR ${fmt$(data.revenue.mrr_cents)} · ${data.revenue.pro_monthly_count} mo · ${data.revenue.pro_yearly_count} yr`}
              testId="kpi-pro"
              accent="#c48b2e"
              category="pro_active"
              onClick={openDrilldown}
            />
            <Kpi
              icon={Calendar}
              label="Events"
              value={data.totals.events_live}
              sub={`${data.totals.events_pending} pending review · ${data.totals.events_cancelled} cancelled`}
              testId="kpi-events"
              accent="var(--moss)"
              category="events_live"
              onClick={openDrilldown}
            />
            <Kpi
              icon={Send}
              label="Applications"
              value={data.totals.applications_total}
              sub={`${data.totals.applications_approved} approved · ${data.totals.applications_pending} pending`}
              testId="kpi-applications"
              accent="#5a7ba6"
              category="applications_total"
              onClick={openDrilldown}
            />
            <Kpi
              icon={DollarSign}
              label="Booth fees collected"
              value={fmt$(data.revenue.booth_fees_gross_cents)}
              sub={`${data.revenue.booth_fees_count} payments`}
              testId="kpi-booth-revenue"
              accent="#2b6b4a"
              category="booth_fees"
              onClick={openDrilldown}
            />
            <Kpi
              icon={ShieldCheck}
              label="Platform fee earned"
              value={fmt$(data.revenue.platform_fee_earned_cents)}
              sub="5% cut on booth payments"
              testId="kpi-platform-fee"
              accent="#2b6b4a"
              category="platform_fee"
              onClick={openDrilldown}
            />
            <Kpi
              icon={ShieldCheck}
              label="Verified organizers"
              value={data.totals.verified_organizers}
              sub={`${data.totals.organizers} total`}
              testId="kpi-verified"
              accent="var(--moss)"
              category="verified_organizers"
              onClick={openDrilldown}
            />
            <Kpi
              icon={Star}
              label="Ratings submitted"
              value={data.totals.ratings_total}
              sub="Bidirectional feedback"
              testId="kpi-ratings"
              accent="#c48b2e"
              category="ratings_total"
              onClick={openDrilldown}
            />
          </div>

          {/* Chart + Feed */}
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <SignupsChart data={data.signups_daily} />
            </div>
            <div
              className="rounded-lg border p-5"
              style={{
                backgroundColor: "#fff",
                borderColor: "rgba(0,0,0,0.08)",
                maxHeight: "560px",
                overflowY: "auto",
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div
                    className="text-[10px] uppercase tracking-widest"
                    style={{ color: "var(--ink-soft)" }}
                  >
                    Live activity
                  </div>
                  <div className="font-serif text-xl mt-0.5">
                    What&apos;s happening now.
                  </div>
                </div>
                <div
                  className="flex items-center gap-1.5 text-[11px]"
                  style={{ color: "var(--moss)" }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full animate-pulse"
                    style={{ backgroundColor: "var(--moss)" }}
                  />
                  Live
                </div>
              </div>
              <ActivityFeed items={data.activity || []} />
            </div>
          </div>

          <div
            className="mt-10 text-[11px] text-right"
            style={{ color: "var(--ink-soft)" }}
          >
            Last refreshed {relTime(data.generated_at)}
          </div>

          <div className="mt-10">
            <StripeSyncReport />
          </div>
        </>
      )}

      <AnalyticsDrilldown
        category={drilldownCat}
        open={Boolean(drilldownCat)}
        onClose={() => setDrilldownCat(null)}
      />
    </div>
  );
}
